import { Terminal, CheckCircle, XCircle } from "lucide-react";
import { useState } from "react";

import { confirmCliAuth } from "@/lib/api";

type Status = "idle" | "confirming" | "success" | "error";

export function CliAuthView() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  // Read user code from URL query params
  const params = new URLSearchParams(window.location.search);
  const userCode = params.get("code");

  const handleConfirm = async () => {
    if (!userCode) return;
    setStatus("confirming");
    setErrorMessage("");

    try {
      await confirmCliAuth(userCode);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Failed to authorize");
    }
  };

  if (!userCode) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <div className="text-center">
          <XCircle size={48} className="mx-auto mb-4 text-red-400" />
          <h1 className="text-cyber-text mb-2 text-lg font-bold">Invalid Link</h1>
          <p className="text-cyber-muted text-sm">
            No authorization code found. Run <code className="text-cyber-cyan">sherlog login</code>{" "}
            to start.
          </p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <div className="text-center">
          <CheckCircle size={48} className="mx-auto mb-4 text-green-400" />
          <h1 className="text-cyber-text mb-2 text-lg font-bold">CLI Authorized</h1>
          <p className="text-cyber-muted text-sm">
            You can close this tab and return to your terminal.
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <div className="text-center">
          <XCircle size={48} className="mx-auto mb-4 text-red-400" />
          <h1 className="text-cyber-text mb-2 text-lg font-bold">Authorization Failed</h1>
          <p className="text-cyber-muted mb-4 text-sm">{errorMessage}</p>
          <p className="text-cyber-muted text-sm">
            Run <code className="text-cyber-cyan">sherlog login</code> again to get a new code.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center">
      <div className="w-full max-w-sm text-center">
        <Terminal size={48} className="text-cyber-cyan mx-auto mb-4" />
        <h1 className="text-cyber-text mb-2 text-lg font-bold">Authorize CLI Access</h1>
        <p className="text-cyber-muted mb-6 text-sm">Verify this code matches your terminal:</p>

        {/* User code display */}
        <div className="border-cyber-cyan/30 bg-cyber-cyan/5 mb-6 rounded-lg border px-6 py-4">
          <span className="text-cyber-cyan font-mono text-3xl font-bold tracking-widest">
            {userCode}
          </span>
        </div>

        <button
          type="button"
          onClick={handleConfirm}
          disabled={status === "confirming"}
          className="border-cyber-cyan/30 bg-cyber-cyan/10 text-cyber-cyan hover:bg-cyber-cyan/20 w-full rounded border px-6 py-3 text-sm font-medium disabled:opacity-50"
        >
          {status === "confirming" ? "Authorizing..." : "Authorize"}
        </button>
      </div>
    </div>
  );
}
